import { useMemo, useState } from 'react';
import BreakdownTable from '@/components/ui/breakdown-tables/BreakdownTable';
import type {
  BreakdownCell,
  BreakdownCellContext,
} from '@/components/ui/breakdown-tables/BreakdownTable';
import type { UnifiedForecastResult, RebateBreakdown } from '@erp/shared-types';
import type { SupplierPayment, PaymentAllocation } from '@/api/rebates/types';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { sortDatesChronologically } from '@/utils/breakdown-transforms/timeFilter';
import {
  aggregateCellValues,
  filterCellsForAggregation,
  getAllocationStatus,
  renderAllocationCell,
  type AllocationCellData,
} from '@/utils/rebates/allocationAggregation';
import {
  formatDatesForDisplay,
  extractColumnOrderFromDateLabels,
  formatDateToString,
  buildAllocationKey,
} from '@/utils/rebates/allocationTableHelpers';

interface ReconciliationBreakdownTableProps {
  forecastData?: UnifiedForecastResult;
  supplierPayments?: SupplierPayment[];
  rules: Array<{ id: string; name: string }>;
  divisions: Array<{ id: string; name: string }>;
}

/**
 * Read-only reconciliation breakdown table
 * Shows expected vs allocated amounts by month, rule, and division
 * Similar structure to PaymentAllocationModal but read-only
 */
export function ReconciliationBreakdownTable({
  forecastData,
  supplierPayments,
  rules,
  divisions,
}: ReconciliationBreakdownTableProps) {
  const [viewByRuleFirst, setViewByRuleFirst] = useState(true);

  // Extract unique dates from forecast data
  const availableDates = useMemo(() => {
    const dateSet = new Set<string>();
    if (forecastData?.estimates) {
      forecastData.estimates.forEach((estimate) => {
        // Extract date from estimate (it's a Date object)
        dateSet.add(formatDateToString(estimate.date));
      });
    }
    return sortDatesChronologically(Array.from(dateSet));
  }, [forecastData]);

  // Format dates for display
  const dateLabels = useMemo(() => {
    return formatDatesForDisplay(availableDates);
  }, [availableDates]);

  // Extract column order for BreakdownTable (chronological order)
  const columnOrder = useMemo(() => {
    return extractColumnOrderFromDateLabels(dateLabels);
  }, [dateLabels]);

  // Build map of expected rebates: ruleId|divisionId|date -> amount
  const expectedRebatesMap = useMemo(() => {
    const map = new Map<string, number>();
    if (forecastData?.estimates) {
      forecastData.estimates.forEach((estimate) => {
        const dateStr = formatDateToString(estimate.date);

        estimate.breakdowns.forEach((breakdown: RebateBreakdown) => {
          const key = buildAllocationKey(
            breakdown.ruleId,
            breakdown.divisionId,
            dateStr,
          );
          const existing = map.get(key) || 0;
          map.set(key, existing + breakdown.rebateAmount);
        });
      });
    }
    return map;
  }, [forecastData]);

  // Build map of allocated amounts: ruleId|divisionId|date -> amount
  const allocatedAmountsMap = useMemo(() => {
    const map = new Map<string, number>();
    supplierPayments?.forEach((payment) => {
      if (payment.allocations) {
        payment.allocations.forEach((allocation: PaymentAllocation) => {
          const key = buildAllocationKey(
            allocation.rebateRuleId,
            allocation.divisionId,
            allocation.date,
          );
          const existing = map.get(key) || 0;
          map.set(key, existing + allocation.allocatedAmount);
        });
      }
    });
    return map;
  }, [supplierPayments]);

  // Transform data into BreakdownCell[] format
  const data = useMemo(() => {
    const cells: BreakdownCell[] = [];

    if (viewByRuleFirst) {
      // Rule × Division view: Rule as row, Division as subRow
      rules.forEach((rule) => {
        const ruleDivisions = divisions.filter((div) => {
          // Check if this rule×division combo has any data
          return availableDates.some((date) => {
            const key = buildAllocationKey(rule.id, div.id, date);
            return expectedRebatesMap.has(key) || allocatedAmountsMap.has(key);
          });
        });

        ruleDivisions.forEach((division) => {
          dateLabels.forEach(({ date, label }) => {
            const key = buildAllocationKey(rule.id, division.id, date);
            const expected = expectedRebatesMap.get(key) || 0;
            const allocated = allocatedAmountsMap.get(key) || 0;
            const variance = allocated - expected;

            cells.push({
              column: label, // Month label (e.g., "Jan 2024")
              subColumn: null,
              row: rule.name, // Rule name
              subRow: division.name, // Division name
              value: variance, // Show variance as primary value for aggregation
              metadata: {
                ruleId: rule.id,
                divisionId: division.id,
                date,
                expected,
                allocated,
                variance,
              },
            });
          });
        });
      });
    } else {
      // Division × Rule view: Division as row, Rule as subRow
      divisions.forEach((division) => {
        const divisionRules = rules.filter((rule) => {
          // Check if this division×rule combo has any data
          return availableDates.some((date) => {
            const key = buildAllocationKey(rule.id, division.id, date);
            return expectedRebatesMap.has(key) || allocatedAmountsMap.has(key);
          });
        });

        divisionRules.forEach((rule) => {
          dateLabels.forEach(({ date, label }) => {
            const key = buildAllocationKey(rule.id, division.id, date);
            const expected = expectedRebatesMap.get(key) || 0;
            const allocated = allocatedAmountsMap.get(key) || 0;
            const variance = allocated - expected;

            cells.push({
              column: label, // Month label
              subColumn: null,
              row: division.name, // Division name
              subRow: rule.name, // Rule name
              value: variance, // Show variance as primary value for aggregation
              metadata: {
                ruleId: rule.id,
                divisionId: division.id,
                date,
                expected,
                allocated,
                variance,
              },
            });
          });
        });
      });
    }

    return cells;
  }, [
    rules,
    divisions,
    dateLabels,
    availableDates,
    expectedRebatesMap,
    allocatedAmountsMap,
    viewByRuleFirst,
  ]);

  // Aggregate allocation data for a given scope (for total rows and group-level aggregations)
  const aggregateAllocationData = useMemo(() => {
    return (
      row: string | undefined,
      column: string | undefined,
      isGroupLevel: boolean,
    ): {
      expectedRebate: number;
      allocated: number;
      delta: number;
    } => {
      let expectedRebate = 0;
      let allocated = 0;

      // Filter cells that match the aggregation scope
      const matchingCells = filterCellsForAggregation(
        data,
        row,
        column,
        isGroupLevel,
      );

      // Aggregate data from matching cells
      matchingCells.forEach((cell) => {
        const metadata = cell.metadata as
          | {
              ruleId?: string;
              divisionId?: string;
              date?: string;
              expected?: number;
              allocated?: number;
              variance?: number;
            }
          | undefined;

        const expected = metadata?.expected || 0;
        const allocatedAmount = metadata?.allocated || 0;

        expectedRebate += expected;
        allocated += allocatedAmount;
      });

      const delta = allocated - expectedRebate;

      return {
        expectedRebate,
        allocated,
        delta,
      };
    };
  }, [data]);

  // Custom cell renderer showing Expected, Allocated, and Delta
  const renderCell = (context: BreakdownCellContext): React.ReactNode => {
    const { cell, row, column, isGroupLevel } = context;
    const metadata = cell.metadata as
      | {
          expected?: number;
          allocated?: number;
          variance?: number;
          date?: string;
        }
      | undefined;

    // Check if this is an aggregated cell (total row or group level)
    const isAggregated = isGroupLevel || row === '__TOTAL__';

    let expectedRebate: number;
    let allocated: number;
    let delta: number;

    if (isAggregated) {
      // For aggregated cells, calculate totals from all matching cells
      const aggregated = aggregateAllocationData(row, column, isGroupLevel);
      expectedRebate = aggregated.expectedRebate;
      allocated = aggregated.allocated;
      delta = aggregated.delta;
    } else {
      // For individual cells, use the cell's metadata
      expectedRebate = metadata?.expected || 0;
      allocated = metadata?.allocated || 0;
      delta = metadata?.variance || 0;
    }

    const cellData: AllocationCellData = {
      expectedRebate,
      allocated,
      delta,
    };

    return renderAllocationCell(context, cellData, {
      editable: false,
    });
  };

  // Aggregation function for cell values (aggregates variance/delta)
  // Uses shared utility function

  return (
    <div className="space-y-2">
      {/* Toggle for view mode */}
      <div className="flex items-center gap-2 justify-end">
        <Label htmlFor="view-toggle" className="text-sm cursor-pointer">
          {viewByRuleFirst ? 'Rule × Division' : 'Division × Rule'}
        </Label>
        <Switch
          id="view-toggle"
          checked={viewByRuleFirst}
          onCheckedChange={setViewByRuleFirst}
        />
      </div>

      <BreakdownTable
        data={data}
        columnLabel="Month"
        rowLabel={viewByRuleFirst ? 'Rule' : 'Division'}
        subRowLabel={viewByRuleFirst ? 'Division' : 'Rule'}
        title="Reconciliation Breakdown by Month"
        showXDim1Total={false}
        showYDim1Total={true}
        renderCell={renderCell}
        aggregateCellValues={aggregateCellValues}
        showCard={false}
        maxHeight={600}
        xDimOrder={columnOrder}
        fillMissingSubColumns={true}
        fillMissingSubRows={true}
      />
    </div>
  );
}
